import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useForm, useController } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { Modal, Tabs } from '@mantine/core';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import {
    Button,
    DoubleCheckModal,
    ControlledCheckbox,
    ControlledInput,
    ControlledTextarea,
    ControlledMultiSelect,
    DateTimeInput,
    RadioGroup,
    Radio,
} from '@shared/ui';
import {
    useCancelScheduledPromotion,
    useScheduledPromotions,
    usePromotionHistory,
    useSendPromotion,
    type PromotionChannel,
    type PromotionHistoryItem,
    type ScheduledPromotion,
} from '@modules/push';
import { useBonusUsers } from '@modules/bonuses';
import { sellersApi } from '@modules/sellers/api/sellers.api';
import { formatPhoneNumber } from '@shared/utils';
import '@shared/styles/page.css';
import './PushPage.css';

type TargetType = 'all' | 'selected';
type AudienceType = 'clients' | 'sellers';

interface PushFormValues {
    title: string;
    body: string;
    audience: AudienceType;
    targetType: TargetType;
    userIds: number[];
    sellerIds: number[];
    scheduledAt: string;
    sendNow: boolean;
    isAdvertising: boolean;
}

const defaultValues: PushFormValues = {
    title: '',
    body: '',
    audience: 'clients',
    targetType: 'all',
    userIds: [],
    sellerIds: [],
    scheduledAt: '',
    sendNow: false,
    isAdvertising: true,
};

const toMoscowIso = (value: string) => new Date(`${value}:00+03:00`).toISOString();

const scheduledDateTimePattern = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/;

const toPickerValue = (value: string) => {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/);
    if (!match) return null;
    return `${match[1]} ${match[2]}:${match[3]}:00`;
};

const fromPickerValue = (value: string | null) => {
    if (!value) return '';
    const match = value.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})/);
    if (!match) return '';
    return `${match[1]}T${match[2]}:${match[3]}`;
};

const formatMoscowDateTime = (value: string) =>
    new Intl.DateTimeFormat('ru-RU', {
        timeZone: 'Europe/Moscow',
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(value));

const promotionTypeLabel = (row: PromotionHistoryItem) => {
    if (row.audience === 'CLIENTS') return 'Пуш клиентам';
    return row.channel === 'EMAIL' ? 'Email продавцам' : 'Пуш продавцам';
};

const promotionStatusLabel = (status: PromotionHistoryItem['status']) =>
    ({
        PROCESSING: 'Отправляется',
        SENT: 'Отправлена',
        FAILED: 'Ошибка',
        CANCELED: 'Отменена',
    })[status];

/** Текст до 3 строк; «Показать полностью» — только если не влез. */
function ClampedText({ text }: { text: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);

    useLayoutEffect(() => {
        const el = ref.current;
        if (el && !expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [text, expanded]);

    return (
        <>
            <div
                ref={ref}
                className={`push-body push-body--wide${expanded ? '' : ' push-body--clamped'}`}
            >
                {text}
            </div>
            {(overflowing || expanded) && (
                <button type="button" className="push-link" onClick={() => setExpanded((v) => !v)}>
                    {expanded ? 'Свернуть' : 'Показать полностью'}
                </button>
            )}
        </>
    );
}

export default function PushPage() {
    const { data: users = [], isLoading: usersLoading } = useBonusUsers();
    const { data: sellers = [], isLoading: sellersLoading } = useQuery({
        queryKey: ['sellers', 'list-for-push'],
        queryFn: () => sellersApi.getAll(),
    });
    const sendPromotion = useSendPromotion();
    const { data: scheduledPromotions = [], isLoading: scheduledPromotionsLoading } =
        useScheduledPromotions();
    const cancelScheduledPromotion = useCancelScheduledPromotion();
    const [cancelTarget, setCancelTarget] = useState<ScheduledPromotion | null>(null);
    const [activeTab, setActiveTab] = useState<PromotionChannel | 'scheduled' | 'history'>('push');
    const { data: promotionHistory = [], isLoading: promotionHistoryLoading } = usePromotionHistory();
    const [recipientsTarget, setRecipientsTarget] = useState<PromotionHistoryItem | null>(null);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<PushFormValues>({
        defaultValues,
    });

    const { field: targetTypeField } = useController({
        name: 'targetType',
        control,
    });
    const { field: audienceField } = useController({
        name: 'audience',
        control,
    });
    const { field: sendNowField } = useController({
        name: 'sendNow',
        control,
    });
    const { field: scheduledAtField } = useController({
        name: 'scheduledAt',
        control,
    });
    const { field: isAdvertisingField } = useController({
        name: 'isAdvertising',
        control,
    });
    const isAdvertising = Boolean(isAdvertisingField.value);
    const targetType = targetTypeField.value as TargetType;
    const isEmail = activeTab === 'email';
    // Email-рассылка — только продавцам, на email директора
    const audience: AudienceType = isEmail ? 'sellers' : (audienceField.value as AudienceType);
    const sendNow = Boolean(sendNowField.value);

    const userOptions = useMemo(
        () =>
            users.map((u) => ({
                value: String(u.id),
                label: u.name ? `${formatPhoneNumber(u.phone)} (${u.name})` : formatPhoneNumber(u.phone),
            })),
        [users],
    );

    const sellerOptions = useMemo(
        () =>
            sellers.map((s) => ({
                value: String(s.id),
                label: s.shopName?.trim() ? `${s.shopName} (#${s.id})` : `Магазин #${s.id}`,
            })),
        [sellers],
    );

    const userLabelById = useMemo(
        () => new Map(userOptions.map((o) => [Number(o.value), o.label])),
        [userOptions],
    );
    const sellerLabelById = useMemo(
        () => new Map(sellerOptions.map((o) => [Number(o.value), o.label])),
        [sellerOptions],
    );

    const filterUserOptions = useCallback(
        (input: { options: unknown[]; search: string }) => {
            const { options, search } = input;
            if (!search.trim()) return options;
            const searchLower = search.toLowerCase().trim();
            const searchDigits = search.replace(/\D/g, '');
            return options.filter((option: unknown) => {
                if (!option || typeof option !== 'object' || !('label' in option)) return true;
                const o = option as { label?: string };
                const label = (o.label ?? '').toString().toLowerCase();
                const phoneDigits = (o.label ?? '').replace(/\D/g, '');
                return label.includes(searchLower) || (searchDigits.length > 0 && phoneDigits.includes(searchDigits));
            });
        },
        [],
    );

    const filterSellerOptions = useCallback((input: { options: unknown[]; search: string }) => {
        const { options, search } = input;
        if (!search.trim()) return options;
        const q = search.toLowerCase().trim();
        return options.filter((option: unknown) => {
            if (!option || typeof option !== 'object' || !('label' in option)) return true;
            const o = option as { label?: string };
            return (o.label ?? '').toString().toLowerCase().includes(q);
        });
    }, []);

    const onSubmit = async (data: PushFormValues) => {
        if (!data.title?.trim()) {
            notifications.show({ title: 'Ошибка', message: 'Введите заголовок', color: 'red' });
            return;
        }
        if (!data.sendNow && !scheduledDateTimePattern.test(data.scheduledAt)) {
            notifications.show({
                title: 'Ошибка',
                message: 'Укажите дату и время рассылки',
                color: 'red',
            });
            return;
        }
        const scheduledAt = data.sendNow ? undefined : toMoscowIso(data.scheduledAt);
        if (scheduledAt && new Date(scheduledAt).getTime() <= Date.now()) {
            notifications.show({
                title: 'Ошибка',
                message: 'Дата и время рассылки должны быть в будущем',
                color: 'red',
            });
            return;
        }
        if (!isEmail && data.audience === 'clients') {
            if (data.targetType === 'selected' && (!data.userIds || data.userIds.length === 0)) {
                notifications.show({
                    title: 'Ошибка',
                    message: 'Выберите хотя бы одного клиента',
                    color: 'red',
                });
                return;
            }
            await sendPromotion.mutateAsync({
                audience: 'clients',
                title: data.title.trim(),
                body: data.body?.trim() || undefined,
                userIds:
                    data.targetType === 'selected' && data.userIds?.length ? data.userIds : undefined,
                isAdvertising: data.isAdvertising,
                scheduledAt,
            });
        } else {
            if (data.targetType === 'selected' && (!data.sellerIds || data.sellerIds.length === 0)) {
                notifications.show({
                    title: 'Ошибка',
                    message: 'Выберите хотя бы одного продавца',
                    color: 'red',
                });
                return;
            }
            await sendPromotion.mutateAsync({
                audience: 'sellers',
                channel: isEmail ? 'email' : 'push',
                title: data.title.trim(),
                body: data.body?.trim() || undefined,
                sellerIds:
                    data.targetType === 'selected' && data.sellerIds?.length
                        ? data.sellerIds
                        : undefined,
                isAdvertising: data.isAdvertising,
                scheduledAt,
            });
        }
        reset(defaultValues);
    };

    const recipientAllLabel =
        audience === 'clients' ? 'Всем клиентам приложения' : 'Всем продавцам';
    const recipientSelectedLabel =
        audience === 'clients' ? 'Выбранным клиентам' : 'Выбранным продавцам';

    const scheduledColumns = useMemo<OptionsTableColumn<ScheduledPromotion>[]>(
        () => [
            {
                key: 'scheduledAt',
                label: 'Дата и время (МСК)',
                render: (row) => formatMoscowDateTime(row.scheduledAt),
            },
            {
                key: 'title',
                label: 'Рассылка',
                render: (row) => (
                    <div>
                        <span className="push-op-label">{row.title}</span>
                        {row.body ? <ClampedText text={row.body} /> : null}
                        <div className="push-body">
                            {row.isAdvertising ? 'Реклама — только с согласием' : 'Информационная — всем'}
                        </div>
                    </div>
                ),
            },
            {
                key: 'recipients',
                label: 'Получатели',
                render: (row) => {
                    const recipientCount = Array.isArray(row.recipientIds) ? row.recipientIds.length : 0;
                    if (row.audience === 'CLIENTS') {
                        return recipientCount ? `Клиенты: ${recipientCount}` : 'Все клиенты';
                    }
                    const sellersLabel = recipientCount ? `Продавцы: ${recipientCount}` : 'Все продавцы';
                    return (
                        <div>
                            {sellersLabel}
                            <div className="push-body">
                                {row.channel === 'EMAIL' ? 'Email директора' : 'Пуш и кабинет'}
                            </div>
                        </div>
                    );
                },
            },
            {
                key: 'status',
                label: 'Статус',
                render: (row) => (row.status === 'PROCESSING' ? 'Отправляется' : 'Запланирована'),
            },
            {
                key: 'cancel',
                label: '',
                render: (row) => (
                    <button
                        type="button"
                        className="push-cancel"
                        disabled={row.status === 'PROCESSING'}
                        onClick={() => setCancelTarget(row)}
                    >
                        Отменить
                    </button>
                ),
            },
        ],
        [],
    );

    const historyColumns = useMemo<OptionsTableColumn<PromotionHistoryItem>[]>(
        () => [
            {
                key: 'date',
                label: 'Дата (МСК)',
                render: (row) => formatMoscowDateTime(row.sentAt ?? row.scheduledAt),
            },
            {
                key: 'type',
                label: 'Тип',
                render: (row) => (
                    <div>
                        <span className="push-op-label">{promotionTypeLabel(row)}</span>
                        <div className="push-body">{row.isAdvertising ? 'Реклама' : 'Информационная'}</div>
                    </div>
                ),
            },
            {
                key: 'title',
                label: 'Рассылка',
                render: (row) => (
                    <div>
                        <span className="push-op-label">{row.title}</span>
                        {row.body ? <ClampedText text={row.body} /> : null}
                    </div>
                ),
            },
            {
                key: 'recipients',
                label: 'Получатели',
                render: (row) => {
                    const selected = Array.isArray(row.recipientIds) ? row.recipientIds.length : 0;
                    return (
                        <div>
                            {selected ? (
                                <button
                                    type="button"
                                    className="push-link"
                                    onClick={() => setRecipientsTarget(row)}
                                >
                                    Выбрано: {selected}
                                </button>
                            ) : row.audience === 'CLIENTS' ? (
                                'Все клиенты'
                            ) : (
                                'Все продавцы'
                            )}
                            {row.sentCount != null ? (
                                <div className="push-body">
                                    {row.channel === 'EMAIL' ? 'Писем' : 'Доставлено'}: {row.sentCount}
                                    {row.failedCount ? `, ошибок: ${row.failedCount}` : ''}
                                </div>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                key: 'status',
                label: 'Статус',
                render: (row) => (
                    <div>
                        {promotionStatusLabel(row.status)}
                        {row.status === 'FAILED' && row.error ? (
                            <div className="push-body">{row.error}</div>
                        ) : null}
                    </div>
                ),
            },
            {
                key: 'author',
                label: 'Кто отправил',
                render: (row) => row.createdByAdmin?.name?.trim() || row.createdByAdmin?.email || '—',
            },
        ],
        [],
    );

    return (
        <div className="page-container push-page">
            <Breadcrumbs
                items={[
                    { label: 'Главная', path: '/' },
                    { label: 'Рассылки' },
                ]}
            />
            <div className="push-page-head">
                <h1 className="push-page-title">Рассылки</h1>
                <p className="push-page-subtitle">
                    Пуш-уведомления клиентам и продавцам, письма продавцам на email директора
                </p>
            </div>
            <div className="push-page__scroll">
            <div className="content-wrapper push-page__content-wrapper">
            <div
                className={`push-page__content${activeTab === 'history' ? ' push-page__content--wide' : ''}`}
            >
            <Tabs
                value={activeTab}
                onChange={(value) => setActiveTab((value as typeof activeTab) ?? 'push')}
                className="push-page__tabs push-tabs"
            >
                <Tabs.List className="push-page__tabs-list push-tabs-list">
                    <Tabs.Tab value="push" className="push-tabs-tab">Пуш-рассылка</Tabs.Tab>
                    <Tabs.Tab value="email" className="push-tabs-tab">Email-рассылка</Tabs.Tab>
                    <Tabs.Tab value="scheduled" className="push-tabs-tab">
                        Запланированные
                        {scheduledPromotions.length > 0 && ` (${scheduledPromotions.length})`}
                    </Tabs.Tab>
                    <Tabs.Tab value="history" className="push-tabs-tab">История</Tabs.Tab>
                </Tabs.List>
                {(activeTab === 'push' || activeTab === 'email') && (
                <div className="push-page__tabs-panel push-tabs-panel">
                    <section className="push-card">
                        {isEmail && (
                            <p className="push-page-subtitle">
                                Письмо продавцам на email директора из заявки
                            </p>
                        )}
                        <form onSubmit={handleSubmit(onSubmit)} className="push-form">
                    <div className="push-form-field">
                        <ControlledInput
                            label={isEmail ? 'Тема письма' : 'Заголовок'}
                            name="title"
                            control={control}
                            error={errors.title}
                            placeholder={isEmail ? 'Тема и заголовок письма' : 'Заголовок уведомления'}
                            required
                        />
                    </div>
                    <div className="push-form-field">
                        <ControlledTextarea
                            label={isEmail ? 'Текст письма' : 'Текст'}
                            name="body"
                            control={control}
                            error={errors.body}
                            placeholder={
                                isEmail
                                    ? 'Текст письма (необязательно). Пустая строка — новый абзац, ссылки станут кликабельными'
                                    : 'Текст уведомления (необязательно)'
                            }
                            minRows={isEmail ? 6 : 3}
                        />
                    </div>
                    <div className="push-form-field">
                        <ControlledCheckbox
                            label="Рекламная рассылка"
                            description={
                                isAdvertising
                                    ? 'Получат только те, кто дал согласие на рекламную рассылку'
                                    : 'Получат все. Только служебная информация — без скидок, акций и призывов к покупке'
                            }
                            name="isAdvertising"
                            control={control}
                        />
                    </div>
                    <div className="push-form-field push-form-row">
                        <DateTimeInput
                            className="push-form-row__grow"
                            label="Дата и время отправки"
                            description="Московское время"
                            placeholder="Выберите дату и время"
                            value={toPickerValue(scheduledAtField.value)}
                            onChange={(value) => scheduledAtField.onChange(fromPickerValue(value))}
                            onBlur={scheduledAtField.onBlur}
                            error={sendNow ? undefined : errors.scheduledAt?.message}
                            disabled={sendNow}
                            clearable
                        />
                        <div className="push-form-row__side">
                            <ControlledCheckbox
                                label="Отправить сейчас"
                                name="sendNow"
                                control={control}
                            />
                        </div>
                    </div>
                    {!isEmail && (
                        <div className="push-form-field">
                            <span className="push-form-label">Аудитория</span>
                            <RadioGroup
                                className="push-radio-group"
                                value={audience}
                                onChange={(v) => audienceField.onChange(v as AudienceType)}
                                onBlur={audienceField.onBlur}
                            >
                                <Radio value="clients" label="Клиенты приложения (push + запись в приложении)" />
                                <Radio
                                    value="sellers"
                                    label="Продавцы (приложение, PWA и уведомление в кабинете)"
                                />
                            </RadioGroup>
                        </div>
                    )}
                    <div className="push-form-field">
                        <span className="push-form-label">Кому отправить</span>
                        <RadioGroup
                            className="push-radio-group"
                            value={targetType}
                            onChange={(v) => targetTypeField.onChange(v as TargetType)}
                            onBlur={targetTypeField.onBlur}
                        >
                            <Radio value="all" label={recipientAllLabel} />
                            <Radio value="selected" label={recipientSelectedLabel} />
                        </RadioGroup>
                    </div>
                    {targetType === 'selected' && audience === 'clients' && (
                        <div className="push-form-field">
                            <ControlledMultiSelect
                                label="Клиенты"
                                name="userIds"
                                control={control}
                                error={errors.userIds as any}
                                data={userOptions}
                                placeholder="Выберите клиентов"
                                searchable
                                clearable
                                disabled={usersLoading}
                                nothingFoundMessage="Клиенты не найдены"
                                filter={filterUserOptions as React.ComponentProps<typeof ControlledMultiSelect>['filter']}
                            />
                        </div>
                    )}
                    {targetType === 'selected' && audience === 'sellers' && (
                        <div className="push-form-field">
                            <ControlledMultiSelect
                                label="Продавцы"
                                name="sellerIds"
                                control={control}
                                error={errors.sellerIds as any}
                                data={sellerOptions}
                                placeholder="Выберите продавцов"
                                searchable
                                clearable
                                disabled={sellersLoading}
                                nothingFoundMessage="Продавцы не найдены"
                                filter={filterSellerOptions as React.ComponentProps<typeof ControlledMultiSelect>['filter']}
                            />
                        </div>
                    )}
                    <div className="push-form-actions">
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={sendPromotion.isPending}
                            loading={sendPromotion.isPending}
                        >
                            {sendNow ? 'Отправить сейчас' : 'Запланировать'}
                        </Button>
                    </div>
                        </form>
                    </section>
                </div>
                )}
                <Tabs.Panel value="scheduled" className="push-page__tabs-panel push-tabs-panel">
                    <section className="push-card">
                        <div className="push-card__head">
                            <h2 className="push-card__title">Запланированные</h2>
                        </div>
                        <OptionsTable<ScheduledPromotion>
                            columns={scheduledColumns}
                            data={scheduledPromotions}
                            loading={scheduledPromotionsLoading}
                            emptyMessage="Нет запланированных рассылок"
                        />
                    </section>
                </Tabs.Panel>
                <Tabs.Panel value="history" className="push-page__tabs-panel push-tabs-panel">
                    <section className="push-card">
                        <div className="push-card__head">
                            <h2 className="push-card__title">История рассылок</h2>
                        </div>
                        <OptionsTable<PromotionHistoryItem>
                            columns={historyColumns}
                            data={promotionHistory}
                            loading={promotionHistoryLoading}
                            emptyMessage="Рассылок пока не было"
                        />
                    </section>
                </Tabs.Panel>
            </Tabs>
            </div>
            </div>
            </div>
            <Modal
                opened={recipientsTarget != null}
                onClose={() => setRecipientsTarget(null)}
                title={`Получатели (${recipientsTarget?.recipientIds?.length ?? 0})`}
                centered
            >
                <ul className="push-recipients-list">
                    {(recipientsTarget?.recipientIds ?? []).map((id) => (
                        <li key={id}>
                            {recipientsTarget?.audience === 'CLIENTS'
                                ? (userLabelById.get(id) ?? `Клиент #${id}`)
                                : (sellerLabelById.get(id) ?? `Магазин #${id}`)}
                        </li>
                    ))}
                </ul>
            </Modal>
            <DoubleCheckModal
                isOpen={cancelTarget != null}
                onClose={() => {
                    if (!cancelScheduledPromotion.isPending) setCancelTarget(null);
                }}
                title="Отменить рассылку?"
                message={
                    cancelTarget
                        ? `Запланированная рассылка «${cancelTarget.title}» не будет отправлена.`
                        : ''
                }
                secondButtonText="Отменить"
                secondButtonAction={() => {
                    if (!cancelTarget) return;
                    cancelScheduledPromotion.mutate(cancelTarget.id, {
                        onSuccess: () => setCancelTarget(null),
                    });
                }}
                isLoading={cancelScheduledPromotion.isPending}
            />
        </div>
    );
}
